import { ErrorCode, type ErrorDetail, type ErrorEnvelope } from './generated/types.gen';

/**
 * Every failure the UI has to render, in one shape.
 *
 * Two things the API guarantees are load-bearing here. Every non-2xx response
 * has the same envelope, so there is one parser rather than one per endpoint.
 * And `code` is stable while `message` is not -- so the UI branches on `code`
 * and never on prose.
 *
 * The server's own message is kept on `serverMessage` and never rendered. It is
 * written for an API caller and it is in English; the text a person reads comes
 * from {@link MESSAGE_BY_CODE} below, in pt-BR.
 */
export type ApiErrorKind =
  /** The server answered, with a status we did not want. */
  | 'http'
  /** The request never got an answer: offline, DNS, TLS, a dropped connection. */
  | 'network'
  /** Something else went wrong on the way. */
  | 'unexpected';

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number | undefined;
  readonly code: ErrorCode | undefined;
  readonly details: readonly ErrorDetail[];
  readonly correlationId: string | undefined;
  /** What the server said, for diagnosis. Never shown to a person. */
  readonly serverMessage: string | undefined;

  constructor(init: {
    kind: ApiErrorKind;
    message: string;
    status?: number | undefined;
    code?: ErrorCode | undefined;
    details?: readonly ErrorDetail[] | undefined;
    correlationId?: string | undefined;
    serverMessage?: string | undefined;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.kind = init.kind;
    this.status = init.status;
    this.code = init.code;
    this.details = init.details ?? [];
    this.correlationId = init.correlationId;
    this.serverMessage = init.serverMessage;
  }

  /** The session is gone: expired, revoked, or the account was deactivated. */
  get isUnauthenticated(): boolean {
    return this.status === 401 || this.code === ErrorCode.UNAUTHORIZED;
  }

  get isForbidden(): boolean {
    return this.status === 403 || this.code === ErrorCode.FORBIDDEN;
  }

  get isNotFound(): boolean {
    return this.status === 404 || this.code === ErrorCode.NOT_FOUND;
  }

  /** Worth offering a retry button for. A 403 is not; a 503 is. */
  get isRetryable(): boolean {
    return this.kind === 'network' || (this.status !== undefined && this.status >= 500);
  }
}

/**
 * What a person reads, per error code. Deliberately vague about the server's
 * internals and deliberately specific about what to do next.
 */
const MESSAGE_BY_CODE: Record<ErrorCode, string> = {
  [ErrorCode.BAD_REQUEST]: 'Não foi possível processar o pedido.',
  [ErrorCode.VALIDATION_ERROR]: 'Confira os campos destacados e tente de novo.',
  // The general form. A screen that collects the three fields composes a
  // specific sentence from `details[].registry_value` instead -- see
  // `registryValues` below -- and this is what is left for one that does not.
  [ErrorCode.IDENTITY_MISMATCH]:
    'Os dados não conferem com o que a Receita Federal tem registrado para este CPF.',
  [ErrorCode.UNAUTHORIZED]: 'Sua sessão terminou. Entre novamente para continuar.',
  [ErrorCode.FORBIDDEN]: 'Você não tem permissão para ver esta informação.',
  [ErrorCode.NOT_FOUND]: 'Não encontramos o que você procurava.',
  [ErrorCode.METHOD_NOT_ALLOWED]: 'Esta operação não está disponível.',
  [ErrorCode.CONFLICT]:
    'Esta informação foi alterada por outra pessoa. Recarregue e tente de novo.',
  [ErrorCode.IDEMPOTENCY_KEY_REQUIRED]: 'Não foi possível concluir a operação com segurança.',
  [ErrorCode.IDEMPOTENCY_KEY_REUSED]: 'Esta operação já foi enviada com outro conteúdo.',
  [ErrorCode.IDEMPOTENCY_KEY_IN_PROGRESS]:
    'Esta operação ainda está sendo processada. Aguarde um instante.',
  [ErrorCode.PAYLOAD_TOO_LARGE]: 'Este arquivo é grande demais. Envie uma versão menor.',
  [ErrorCode.UNSUPPORTED_MEDIA_TYPE]:
    'Este tipo de arquivo não é aceito. Envie um PDF ou uma imagem do documento.',
  [ErrorCode.UNPROCESSABLE_ENTITY]: 'Confira os campos destacados e tente de novo.',
  [ErrorCode.TOO_MANY_REQUESTS]: 'Muitas tentativas em pouco tempo. Aguarde um instante.',
  [ErrorCode.INTERNAL_ERROR]:
    'O sistema falhou ao responder. Tente de novo em instantes; se persistir, fale com o suporte.',
  [ErrorCode.SERVICE_UNAVAILABLE]:
    'O sistema está temporariamente indisponível. Tente de novo em instantes.',
};

const NETWORK_MESSAGE =
  'Não foi possível falar com o sistema. Verifique sua conexão e tente de novo.';
const UNEXPECTED_MESSAGE = 'Algo deu errado. Tente de novo em instantes.';

function isErrorEnvelope(value: unknown): value is ErrorEnvelope {
  if (typeof value !== 'object' || value === null || !('error' in value)) {
    return false;
  }
  const body: unknown = (value as { error: unknown }).error;
  return typeof body === 'object' && body !== null && 'code' in body && 'message' in body;
}

function knownCode(value: unknown): ErrorCode | undefined {
  return Object.values(ErrorCode).includes(value as ErrorCode) ? (value as ErrorCode) : undefined;
}

/**
 * Turn whatever the generated client handed back into an {@link ApiError}.
 *
 * The client reports a request that never reached the server as an error with
 * no `response`, which is exactly the distinction the UI needs: a dropped
 * connection is the user's problem to retry, a 500 is ours to fix.
 */
export function toApiError(error: unknown, response?: Response | undefined): ApiError {
  if (error instanceof ApiError) {
    return error;
  }

  if (response === undefined) {
    return new ApiError({
      kind: 'network',
      message: NETWORK_MESSAGE,
      serverMessage: error instanceof Error ? error.message : undefined,
    });
  }

  if (isErrorEnvelope(error)) {
    const body = error.error;
    const code = knownCode(body.code);
    return new ApiError({
      kind: 'http',
      message: code ? MESSAGE_BY_CODE[code] : UNEXPECTED_MESSAGE,
      status: response.status,
      code,
      details: body.details ?? [],
      correlationId: body.correlation_id ?? undefined,
      serverMessage: body.message,
    });
  }

  // A non-2xx that did not carry the documented envelope: a proxy, a gateway,
  // anything in front of the API. Report it by status alone.
  return new ApiError({
    kind: 'http',
    message:
      response.status >= 500 ? MESSAGE_BY_CODE[ErrorCode.INTERNAL_ERROR] : UNEXPECTED_MESSAGE,
    status: response.status,
  });
}

/** The pt-BR sentence for a code, for callers that have a code but no error. */
export function messageForCode(code: ErrorCode): string {
  return MESSAGE_BY_CODE[code];
}

/** Whether this refusal is the CPF registry contradicting what was filed. */
export function isIdentityMismatch(error: unknown): boolean {
  return error instanceof ApiError && error.code === ErrorCode.IDENTITY_MISMATCH;
}

/**
 * What an external registry holds, keyed by the field it disagreed with.
 *
 * The structured half of an `identity_mismatch`, and the only half a screen may
 * read: the envelope's `message` is English prose, and `details[].message`
 * repeats it. A screen composes its own pt-BR sentence from these values.
 *
 * Empty when the registry has no record at all -- a CPF it has never issued --
 * which is a refusal with nothing to correct rather than a missing value.
 */
export function registryValues(error: unknown): Record<string, string> {
  if (!isIdentityMismatch(error)) {
    return {};
  }
  const values: Record<string, string> = {};
  for (const detail of (error as ApiError).details) {
    const field = detail.field;
    const value = detail.registry_value;
    if (field === null || field === undefined || value === null || value === undefined) {
      continue;
    }
    // The server names a field by the same dotted path it validates on, so
    // `body.delta.full_name` and `full_name` are the same field.
    const segments = field.split('.');
    const name = segments[segments.length - 1];
    if (name !== undefined && !(name in values)) {
      values[name] = value;
    }
  }
  return values;
}
